/**
 * @file src/lib/client-logger.ts
 *
 * Client-side logger that mirrors the server logger API. Wraps console methods
 * and emits structured OTLP logs to PostHog, mirroring what the server logger
 * does via instrumentation.ts.
 *
 * Usage:
 *   import { clientLogger } from "@/lib/client-logger";
 *   clientLogger.info("[MyComponent] Something happened", { userId });
 *   clientLogger.error("[MyComponent] Something failed", { error: err });
 */

"use client";

import { SeverityNumber } from "@opentelemetry/api-logs";
import { BatchLogRecordProcessor, LoggerProvider } from "@opentelemetry/sdk-logs";
import { OTLPLogExporter } from "@opentelemetry/exporter-logs-otlp-http";
import { resourceFromAttributes } from "@opentelemetry/resources";
import posthog from "posthog-js";
import { sendGAEvent } from "@next/third-parties/google";

// Capture original console methods before any third-party SDK (like PostHog exception auto-capturer) monkeypatches them.
// This prevents duplicate exception capture in PostHog since PostHog's capture_console_errors is enabled.
const originalConsole = typeof console !== "undefined" ? {
  log: console.log,
  info: console.info,
  warn: console.warn,
  error: console.error,
  debug: console.debug,
} : null;

const getLogUrl = () => {
  if (typeof window !== "undefined") {
    return `${window.location.origin}/hc-events/i/v1/logs`;
  }
  return "https://us.i.posthog.com/i/v1/logs";
};

const clientLoggerProvider = new LoggerProvider({
  resource: resourceFromAttributes({ "service.name": "hirecarta" }),
  processors: [
    new BatchLogRecordProcessor({
      exporter: new OTLPLogExporter({
        url: getLogUrl(),
        headers: {
          Authorization: `Bearer ${process.env.NEXT_PUBLIC_POSTHOG_KEY}`,
          "Content-Type": "application/json",
        },
      }),
    }),
  ],
});

const otel = clientLoggerProvider.getLogger("hirecarta");

type Attributes = Record<string, unknown>;

/**
 * Helper to automatically enrich log attributes with the active PostHog session ID.
 */
function getAttributesWithSession(attributes?: Attributes): Record<string, string | number | boolean> | undefined {
  const normalized: Record<string, any> = attributes ? { ...attributes } : {};

  // Map user_email -> email
  if ('user_email' in normalized) {
    normalized.email = normalized.user_email;
    delete normalized.user_email;
  }

  // Map id -> user_id if user_id is missing
  if ('id' in normalized && !('user_id' in normalized)) {
    normalized.user_id = normalized.id;
  }

  try {
    const sessionId = posthog.get_session_id?.();
    if (sessionId) {
      normalized.session_id = sessionId;
      normalized.posthogSessionId = sessionId;
    }

    // Auto-populate email from posthog if missing and user is identified (email contains @)
    if (!normalized.email) {
      const distinctId = posthog.get_distinct_id?.();
      if (distinctId && distinctId.includes('@')) {
        normalized.email = distinctId;
      }
    }
  } catch {
    // Silently ignore if posthog is not initialized or errors out
  }
  return Object.keys(normalized).length > 0
    ? (normalized as Record<string, string | number | boolean>)
    : undefined;
}

/**
 * Safely extracts a string error message from any value/object without ever throwing.
 */
const getErrorMessage = (err: any): string => {
  if (!err) return '';
  if (err instanceof Error) {
    return err.message;
  }
  if (typeof err === 'object') {
    if ('message' in err && typeof err.message === 'string') {
      return err.message;
    }
    if ('message' in err && err.message) {
      return getErrorMessage(err.message);
    }
    if ('code' in err && typeof err.code === 'string') {
      if ('details' in err && typeof err.details === 'string') {
        return `${err.code}: ${err.details}`;
      }
      return err.code;
    }
    try {
      return JSON.stringify(err);
    } catch {
      // Safe fallback for circular references
      try {
        const simpleObj: Record<string, string> = {};
        for (const k in err) {
          if (Object.prototype.hasOwnProperty.call(err, k)) {
            const type = typeof err[k];
            if (type === 'string' || type === 'number' || type === 'boolean') {
              simpleObj[k] = String(err[k]);
            } else {
              simpleObj[k] = `[${type}]`;
            }
          }
        }
        return JSON.stringify(simpleObj);
      } catch {
        return String(err);
      }
    }
  }
  return String(err);
};

/**
 * Helper to identify benign network errors (such as fetch requests aborted by the browser
 * during navigation/page-unload, or temporary offline states) that should not be tracked as errors.
 */
const isBenignNetworkError = (err: any): boolean => {
  if (!err) return false;
  const message = getErrorMessage(err);
  const name = err.name || '';
  return (
    name === 'AbortError' ||
    message === 'Failed to fetch' ||
    message.includes('NetworkError') ||
    message.includes('network error') ||
    message.includes('aborted') ||
    message === 'Load failed'
  );
};

export const clientLogger = {
  debug(message: string, attributes?: Attributes): void {
    if (originalConsole?.debug) {
      originalConsole.debug(message, attributes ?? "");
    } else {
      console.debug(message, attributes ?? "");
    }
    otel.emit({
      body: message,
      severityNumber: SeverityNumber.DEBUG,
      attributes: getAttributesWithSession(attributes),
    });
  },

  info(message: string, attributes?: Attributes): void {
    if (originalConsole?.log) {
      originalConsole.log(message, attributes ?? "");
    } else {
      console.log(message, attributes ?? "");
    }
    otel.emit({
      body: message,
      severityNumber: SeverityNumber.INFO,
      attributes: getAttributesWithSession(attributes),
    });
  },

  warn(message: string, attributes?: Attributes): void {
    if (originalConsole?.warn) {
      originalConsole.warn(message, attributes ?? "");
    } else {
      console.warn(message, attributes ?? "");
    }
    otel.emit({
      body: message,
      severityNumber: SeverityNumber.WARN,
      attributes: getAttributesWithSession(attributes),
    });
  },

  error(message: string, attributes?: Attributes): void {
    // If it's a network cancellation or browser abort, downgrade to warning
    if (attributes?.['error'] && isBenignNetworkError(attributes['error'])) {
      const err = attributes['error'];
      const errorMsg = getErrorMessage(err);
      
      const { error: _, ...otherAttrs } = attributes || {};
      const warnAttrs: Attributes = {
        ...otherAttrs,
        errorMessage: errorMsg,
        isNetworkAbort: true
      };
      
      clientLogger.warn(`${message.trim().replace(/:$/, '')}: aborted (network/abort)`, warnAttrs);
      return;
    }

    let consoleErrorArgs: any[] = [message];
    if (attributes) {
      if (attributes['error']) {
        const errObj = attributes['error'];
        let errStr = '';
        if (errObj instanceof Error) {
          errStr = `${errObj.name}: ${errObj.message}\n${errObj.stack}`;
        } else {
          const msg = getErrorMessage(errObj);
          let details = '';
          try {
            details = JSON.stringify(errObj, null, 2);
          } catch {
            details = getErrorMessage(errObj);
          }
          errStr = msg && msg !== details ? `${msg}\nDetails: ${details}` : details;
        }
        consoleErrorArgs.push(`\nError details: ${errStr}`);
        
        const otherAttrs = { ...attributes };
        delete otherAttrs['error'];
        if (Object.keys(otherAttrs).length > 0) {
          consoleErrorArgs.push(otherAttrs);
        }
      } else {
        consoleErrorArgs.push(attributes);
      }
    }
    if (originalConsole?.error) {
      originalConsole.error(...consoleErrorArgs);
    } else {
      console.error(...consoleErrorArgs);
    }

    const finalAttributes = getAttributesWithSession(attributes);
    otel.emit({
      body: message,
      severityNumber: SeverityNumber.ERROR,
      attributes: finalAttributes,
    });
    try {
      // If attributes contain an active Error object, use it for the full stack trace.
      // Otherwise synthesize one so PostHog can display a stack trace pointing to the caller.
      let err: Error;
      if (attributes?.['error'] instanceof Error) {
        err = attributes['error'];
      } else if (attributes?.['error']) {
        const errorVal = attributes['error'];
        const errorMsg = getErrorMessage(errorVal);
        err = new Error(`${message}: ${errorMsg}`);
        if (typeof errorVal === 'object' && errorVal !== null && 'stack' in errorVal) {
          err.stack = String((errorVal as any).stack);
        }
      } else {
        err = new Error(message);
      }

      posthog.captureException(err, {
        logger_message: message,
        ...finalAttributes,
      });
    } catch {
      // Silently fail — don't let error tracking block execution
    }
  },
};

if (typeof window !== 'undefined') {
  (window as any).clientLogger = clientLogger;
}

/**
 * Track a named event to PostHog from client-side code.
 * Use this for user interaction events (clicks, form submissions, feature usage).
 * For plain logging, use clientLogger instead.
 *
 * Usage:
 *   trackEvent("resume_downloaded", { user_id: session.user.id, format: "pdf" });
 */
export function trackEvent(eventName: string, properties?: Record<string, unknown>): void {
  let finalProperties = properties;
  if (properties && ('user_email' in properties || 'id' in properties)) {
    finalProperties = { ...properties };
    if ('user_email' in finalProperties) {
      finalProperties.email = finalProperties.user_email;
      delete finalProperties.user_email;
    }
    if ('id' in finalProperties && !('user_id' in finalProperties)) {
      finalProperties.user_id = finalProperties.id;
    }
  }
  try {
    posthog.capture(eventName, finalProperties);
  } catch {
    // Silently fail — don't let tracking block execution
  }
  try {
    sendGAEvent("event", eventName, finalProperties ?? {});
  } catch {
    // Silently fail
  }
}

/**
 * Flush pending PostHog events. Call this before hard navigations (OAuth redirects,
 * router.push to API routes) to ensure queued events are sent before the page unloads.
 */
export function flushEvents(): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, 100));
}
